import type { Answers, ControlItem, ControlReview, ControlValue, Template, SurveyContext } from './schema.js';
import { isItemVisible } from './rules.js';

/** The answer reporting uses: the analyst's correction if any, else the reporter's. */
export function confirmedValue(reporter: ControlValue | null, review?: ControlReview): ControlValue | null {
  if (review?.decision === 'CORRECT' && review.correctedValue) return review.correctedValue;
  return reporter;
}

/** The finding's severity: the analyst's override if any, else the template's. */
export function effectiveSeverity(control: Pick<ControlItem, 'severity'>, review?: ControlReview): ControlItem['severity'] {
  return review?.severity ?? control.severity;
}

const SEVERITY_RANK = { LOW: 0, MEDIUM: 1, HIGH: 2, CRITICAL: 3 } as const;

/** Core controls can be raised at review but never lowered (ADR-0010). */
export function reviewOverrideIssue(control: Pick<ControlItem, 'severity' | 'core'>, review?: ControlReview): 'CORE_SEVERITY_LOWERED' | null {
  if (!review?.severity || !control.core) return null;
  return SEVERITY_RANK[review.severity] < SEVERITY_RANK[control.severity] ? 'CORE_SEVERITY_LOWERED' : null;
}

/** N/A is never a finding. Unanswered is not a finding either (validation catches it). */
export function isNonCompliant(control: Pick<ControlItem, 'compliantAnswer'>, value: ControlValue | null): boolean {
  if (value === null || value === 'NA') return false;
  return value !== control.compliantAnswer;
}

export interface DerivedFinding {
  controlCode: string;
  sectionCode: string;
  severity: ControlItem['severity'];
  core: boolean;
  reporterValue: ControlValue | null;
  confirmedValue: ControlValue;
  corrected: boolean;
  /** The analyst's due date, when they set one at review. */
  dueDateOverride: string | null;
}

export interface VisibleControl {
  sectionCode: string;
  control: ControlItem;
}

export function visibleControls(template: Template, answers: Answers, context: SurveyContext): VisibleControl[] {
  const scope = { answers, context };
  const out: VisibleControl[] = [];
  for (const s of template.sections) {
    for (const it of s.items) {
      if (it.kind === 'control' && isItemVisible(s, it, scope)) out.push({ sectionCode: s.code, control: it });
    }
  }
  return out;
}

/** Findings are created for every visible control whose confirmed answer is non-compliant. */
export function deriveFindings(
  template: Template,
  answers: Answers,
  reviews: Record<string, ControlReview>,
  context: SurveyContext,
): DerivedFinding[] {
  const out: DerivedFinding[] = [];
  for (const { sectionCode, control } of visibleControls(template, answers, context)) {
    const reporter = answers.controls[control.code]?.value ?? null;
    const review = reviews[control.code];
    const confirmed = confirmedValue(reporter, review);
    if (confirmed && isNonCompliant(control, confirmed)) {
      out.push({
        controlCode: control.code,
        sectionCode,
        severity: effectiveSeverity(control, review),
        core: control.core,
        reporterValue: reporter,
        confirmedValue: confirmed,
        corrected: review?.decision === 'CORRECT',
        dueDateOverride: review?.dueDate ?? null,
      });
    }
  }
  return out;
}

export interface ComplianceSummary {
  applicable: number;
  compliant: number;
  nonCompliant: number;
  notApplicable: number;
  unanswered: number;
  /** compliant / applicable, 0–100, null when nothing is applicable */
  percent: number | null;
}

function summarise(
  controls: VisibleControl[],
  valueOf: (c: ControlItem) => ControlValue | null,
): ComplianceSummary {
  const s: ComplianceSummary = { applicable: 0, compliant: 0, nonCompliant: 0, notApplicable: 0, unanswered: 0, percent: null };
  for (const { control } of controls) {
    const v = valueOf(control);
    if (v === null) s.unanswered++;
    else if (v === 'NA') s.notApplicable++;
    else {
      s.applicable++;
      if (isNonCompliant(control, v)) s.nonCompliant++;
      else s.compliant++;
    }
  }
  s.percent = s.applicable === 0 ? null : Math.round((s.compliant / s.applicable) * 1000) / 10;
  return s;
}

/** Overall and per-section compliance using confirmed answers when reviews are given. */
export function complianceSummary(
  template: Template,
  answers: Answers,
  context: SurveyContext,
  reviews: Record<string, ControlReview> = {},
): { overall: ComplianceSummary; bySection: Record<string, ComplianceSummary> } {
  const controls = visibleControls(template, answers, context);
  const valueOf = (c: ControlItem) => confirmedValue(answers.controls[c.code]?.value ?? null, reviews[c.code]);
  const bySection: Record<string, ComplianceSummary> = {};
  for (const code of new Set(controls.map((c) => c.sectionCode))) {
    bySection[code] = summarise(controls.filter((c) => c.sectionCode === code), valueOf);
  }
  return { overall: summarise(controls, valueOf), bySection };
}
